/**
 * Palm Access Guard Component
 * Phase 2.5 - Route Protection for Palm Oracle
 * 
 * Access States:
 * - accessLoading: Checking access with backend
 * - accessGranted: User has access to Palm Oracle
 * - accessDenied: User doesn't have access (show upgrade)
 * - readingLoading: Loading specific reading
 * - readingReady: Reading loaded successfully
 * - readingNotFound: Reading doesn't exist
 * - readingForbidden: User doesn't own this reading
 * - readingFailed: Failed to load reading
 * 
 * Rules:
 * - Never redirect while auth is initializing
 * - Backend /api/palm/access is source of truth
 * - Reading ownership verified by backend
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, Lock, AlertCircle, ArrowLeft, LogIn } from 'lucide-react';
import { useAuth, AUTH_STATE } from '../../contexts/AuthContext';
import { checkPalmAccess, getReadingResult } from './services/palmApi';
import './styles/palm.css';

// Access state enum
export const ACCESS_STATE = {
  LOADING: 'accessLoading',
  GRANTED: 'accessGranted',
  DENIED: 'accessDenied'
};

// Reading state enum
export const READING_STATE = {
  LOADING: 'readingLoading',
  READY: 'readingReady',
  NOT_FOUND: 'readingNotFound',
  FORBIDDEN: 'readingForbidden',
  FAILED: 'readingFailed'
};

/**
 * Loading Screen Component
 */
const LoadingScreen = ({ message }) => (
  <div className="palm-guard-loading" data-testid="palm-guard-loading">
    <motion.div
      animate={{ rotate: 360 }}
      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
    >
      <Loader2 size={48} />
    </motion.div>
    <p>{message || 'Yükleniyor...'}</p>
  </div>
);

/**
 * Login Required Screen
 */
const LoginRequiredScreen = ({ onLogin, returnUrl }) => {
  const navigate = useNavigate();
  const { setReturnUrl } = useAuth();

  const handleLogin = () => {
    // Save intended URL for post-login redirect
    if (returnUrl) {
      setReturnUrl(returnUrl);
    }
    // Navigate to home with auth modal
    navigate('/?showAuth=login');
  };

  return (
    <div className="palm-guard-login" data-testid="palm-guard-login-required">
      <LogIn size={48} />
      <h2>Giriş Yapın</h2>
      <p>Palm Oracle'a erişmek için giriş yapmanız gerekiyor.</p>
      <button className="palm-btn palm-btn-primary" onClick={handleLogin}>
        Giriş Yap
      </button>
      <button className="palm-btn palm-btn-ghost" onClick={() => navigate('/')}>
        <ArrowLeft size={18} />
        Ana Sayfa
      </button>
    </div>
  );
};

/**
 * Access Denied Screen (Upgrade Required)
 */
const AccessDeniedScreen = ({ reason, showComingSoon }) => {
  const navigate = useNavigate();

  if (showComingSoon) {
    return (
      <div className="palm-guard-coming-soon" data-testid="palm-guard-coming-soon">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="coming-soon-icon"
        >
          ✋
        </motion.div>
        <h2>Palm Oracle</h2>
        <p>Yakında sizlerle! AI destekli el falı deneyimi için hazırlanıyoruz.</p>
        <button className="palm-btn palm-btn-secondary" onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          Ana Sayfa
        </button>
      </div>
    );
  }

  return (
    <div className="palm-guard-denied" data-testid="palm-guard-access-denied">
      <Lock size={48} />
      <h2>Premium Özellik</h2>
      <p>Palm Oracle'a erişmek için Premium üyelik gereklidir.</p>
      <button 
        className="palm-btn palm-btn-primary" 
        onClick={() => navigate('/?upgrade=palm')}
      >
        Premium'a Geç
      </button>
      <button className="palm-btn palm-btn-ghost" onClick={() => navigate('/')}>
        <ArrowLeft size={18} />
        Ana Sayfa
      </button>
    </div>
  );
};

/**
 * Reading Error Screen
 */
const ReadingErrorScreen = ({ state, readingId }) => {
  const navigate = useNavigate();

  const messages = {
    [READING_STATE.NOT_FOUND]: {
      title: 'Okuma Bulunamadı',
      text: 'Bu el falı okuması bulunamadı veya silinmiş olabilir.'
    },
    [READING_STATE.FORBIDDEN]: {
      title: 'Erişim Reddedildi',
      text: 'Bu el falı okumasına erişim yetkiniz yok.'
    },
    [READING_STATE.FAILED]: {
      title: 'Bir Hata Oluştu',
      text: 'Okuma yüklenirken bir hata oluştu. Lütfen tekrar deneyin.'
    }
  };

  const { title, text } = messages[state] || messages[READING_STATE.FAILED];

  return (
    <div className="palm-guard-error" data-testid={`palm-guard-${state}`}>
      <AlertCircle size={48} />
      <h2>{title}</h2>
      <p>{text}</p>
      <div className="palm-guard-actions">
        <button 
          className="palm-btn palm-btn-primary" 
          onClick={() => navigate('/palm')}
        >
          Palm Oracle'a Dön
        </button>
        <button className="palm-btn palm-btn-ghost" onClick={() => navigate('/')}>
          <ArrowLeft size={18} />
          Ana Sayfa
        </button>
      </div>
    </div>
  );
};

/**
 * Palm Access Guard HOC
 * Wraps Palm pages and handles auth/access/reading states
 */
export function PalmAccessGuard({ 
  children, 
  requireReading = false,
  readingId = null 
}) {
  const { authState, user, isInitializing, isAuthenticated, isUnauthenticated } = useAuth();
  const location = useLocation();
  
  const [accessState, setAccessState] = useState(ACCESS_STATE.LOADING);
  const [accessData, setAccessData] = useState(null);
  
  const [readingState, setReadingState] = useState(requireReading ? READING_STATE.LOADING : null);
  const [readingData, setReadingData] = useState(null);

  // Check access when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      checkAccess();
    }
  }, [isAuthenticated]);

  // Check reading when access granted and reading required
  useEffect(() => {
    if (accessState === ACCESS_STATE.GRANTED && requireReading && readingId) {
      checkReading();
    }
  }, [accessState, requireReading, readingId]);

  /**
   * Check user's access to Palm Oracle via backend
   */
  const checkAccess = async () => {
    setAccessState(ACCESS_STATE.LOADING);
    
    try {
      const token = localStorage.getItem('token');
      const data = await checkPalmAccess(token);
      setAccessData(data);
      
      if (data.has_access) {
        setAccessState(ACCESS_STATE.GRANTED);
      } else {
        setAccessState(ACCESS_STATE.DENIED);
      }
    } catch (error) {
      console.error('Access check failed:', error);
      // On error, default to denied
      setAccessState(ACCESS_STATE.DENIED);
      setAccessData({ has_access: false, reason: 'error', show_locked: true });
    }
  };

  /**
   * Check reading ownership and load data
   */
  const checkReading = async () => {
    setReadingState(READING_STATE.LOADING);
    
    try {
      const token = localStorage.getItem('token');
      const data = await getReadingResult(token, readingId);
      
      setReadingData(data);
      setReadingState(READING_STATE.READY);
    } catch (error) {
      console.error('Reading check failed:', error);
      
      // Determine error type from response
      const status = error.response?.status || error.status;
      
      if (status === 404) {
        setReadingState(READING_STATE.NOT_FOUND);
      } else if (status === 403) {
        setReadingState(READING_STATE.FORBIDDEN);
      } else {
        setReadingState(READING_STATE.FAILED);
      }
    }
  };

  // ========== Render Logic ==========

  // 1. Auth initializing - show loading
  if (isInitializing) {
    return (
      <div className="palm-container palm-guard">
        <LoadingScreen message="Kimlik doğrulanıyor..." />
      </div>
    );
  }

  // 2. Unauthenticated - show login required
  if (isUnauthenticated) {
    return (
      <div className="palm-container palm-guard">
        <LoginRequiredScreen returnUrl={location.pathname} />
      </div>
    );
  }

  // 3. Authenticated but access loading
  if (accessState === ACCESS_STATE.LOADING) {
    return (
      <div className="palm-container palm-guard">
        <LoadingScreen message="Erişim kontrol ediliyor..." />
      </div>
    );
  }

  // 4. Access denied
  if (accessState === ACCESS_STATE.DENIED) {
    return (
      <div className="palm-container palm-guard">
        <AccessDeniedScreen 
          reason={accessData?.reason}
          showComingSoon={accessData?.show_coming_soon}
        />
      </div>
    );
  }

  // 5. Access granted, but reading required and loading
  if (requireReading && readingState === READING_STATE.LOADING) {
    return (
      <div className="palm-container palm-guard">
        <LoadingScreen message="Okuma yükleniyor..." />
      </div>
    );
  }

  // 6. Reading error states
  if (requireReading && readingState && readingState !== READING_STATE.READY) {
    return (
      <div className="palm-container palm-guard">
        <ReadingErrorScreen state={readingState} readingId={readingId} />
      </div>
    );
  }

  // 7. All checks passed - render children
  // Pass access and reading data to children
  return typeof children === 'function' 
    ? children({ accessData, readingData, user })
    : React.cloneElement(children, { accessData, readingData, user });
}

/**
 * HOC version of PalmAccessGuard
 */
export function withPalmAccess(Component, options = {}) {
  return function PalmAccessWrapper(props) {
    return (
      <PalmAccessGuard {...options}>
        {(guardProps) => <Component {...props} {...guardProps} />}
      </PalmAccessGuard>
    );
  };
}

export default PalmAccessGuard;
